// Centralised connection-health state.
//
// Three orthogonal signals shape the UI:
//   - `network` — driven by browser online/offline events. Off implies offline.
//   - `backend` — derived from Firestore listener errors aggregated over a
//     rolling window. `healthy` → no banner; `degraded` → "slow" banner;
//     `down` → banner with manual retry button.
//   - `bannerVisible` / `bannerMessage` / `bannerKind` — derived UI surface.
//
// Listener code reports errors via `recordListenerError`; successful snapshots
// implicitly clear `degraded` via `noteSuccessfulSnapshot`. The state is the
// single source of truth for `App.vue` (banner + popup) and the catastrophic
// `/connection-error` page (manual retry).

const CONNECTION_ERROR_CODES = [
  "unavailable",
  "deadline-exceeded",
  "cancelled",
  "internal",
];

const ROLLING_WINDOW_MS = 30000;
// Trip degraded after N connection-class errors within the rolling window.
const DEGRADED_ERROR_THRESHOLD = 2;
// Manual retry attempts before we route to the catastrophic error page.
const MAX_MANUAL_RETRIES_BEFORE_PAGE = 3;

function nowMs() {
  return Date.now();
}

function pruneOldErrors(errors, windowMs) {
  const cutoff = nowMs() - windowMs;
  return errors.filter((e) => e.ts >= cutoff);
}

export default {
  namespaced: true,
  state: {
    // 'online' | 'offline'
    network: typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "online",
    // 'healthy' | 'degraded' | 'down'
    backend: "healthy",
    // [{ ts: number, code: string, source: string }]
    recentErrors: [],
    // Number of listener wrappers awaiting their first snapshot (informational).
    pendingFirstSnapshots: 0,
    // Banner surface
    bannerVisible: false,
    bannerMessage: "",
    bannerKind: "info", // 'info' | 'warning' | 'error'
    // Manual retry tracking
    manualRetryAttempts: 0,
    lastRetryAt: 0,
  },

  getters: {
    isOffline: (state) => state.network === "offline",
    isDegraded: (state) => state.backend === "degraded",
    isDown: (state) => state.backend === "down",
    recentConnectionErrors: (state) =>
      state.recentErrors.filter((e) => CONNECTION_ERROR_CODES.includes(e.code)),
  },

  mutations: {
    setNetwork(state, val) {
      state.network = val;
    },
    setBackend(state, val) {
      state.backend = val;
    },
    pushError(state, err) {
      state.recentErrors = pruneOldErrors(
        [...state.recentErrors, err],
        ROLLING_WINDOW_MS
      );
    },
    clearErrors(state) {
      state.recentErrors = [];
    },
    incPending(state) {
      state.pendingFirstSnapshots += 1;
    },
    decPending(state) {
      state.pendingFirstSnapshots = Math.max(0, state.pendingFirstSnapshots - 1);
    },
    setBanner(state, payload) {
      state.bannerVisible = !!(payload && payload.visible);
      state.bannerMessage = (payload && payload.message) || "";
      state.bannerKind = (payload && payload.kind) || "info";
    },
    setManualRetryAttempts(state, val) {
      state.manualRetryAttempts = val;
      state.lastRetryAt = nowMs();
    },
    resetManualRetry(state) {
      state.manualRetryAttempts = 0;
      state.lastRetryAt = 0;
    },
  },

  actions: {
    // Called by the network watcher (libs/firestoreHealth.js) on
    // window 'online' / 'offline' events.
    setNetworkOnline({ commit, state, dispatch }) {
      const wasOffline = state.network === "offline";
      commit("setNetwork", "online");
      if (wasOffline) {
        // Coming back from network outage: clear errors and reset backend.
        // Firestore SDK will auto-resync listeners; if real backend issues
        // persist, listener errors will flip backend to degraded again.
        dispatch("markBackendHealthy");
      }
    },
    setNetworkOffline({ commit }) {
      commit("setNetwork", "offline");
    },

    // Called from `subscribeWithHealth` on any error in an onSnapshot.
    recordListenerError({ commit, state, dispatch }, { code, source }) {
      const err = { ts: nowMs(), code: code || "unknown", source: source || "unknown" };
      commit("pushError", err);

      // Only connection-class errors should trip degraded — permission-denied
      // or not-found mean we reached Firestore fine and got a real answer.
      if (!CONNECTION_ERROR_CODES.includes(err.code)) return;

      const recentConnection = pruneOldErrors(
        state.recentErrors,
        ROLLING_WINDOW_MS
      ).filter((e) => CONNECTION_ERROR_CODES.includes(e.code));

      if (
        state.backend === "healthy" &&
        recentConnection.length >= DEGRADED_ERROR_THRESHOLD
      ) {
        dispatch("markBackendDegraded");
      }
    },

    // Called from `subscribeWithHealth` when a listener delivers a snapshot.
    noteSuccessfulSnapshot({ state, dispatch }) {
      if (state.backend !== "healthy") {
        dispatch("markBackendHealthy");
      }
    },

    markBackendDegraded({ commit }) {
      commit("setBackend", "degraded");
      commit("setBanner", {
        visible: true,
        message: "การเชื่อมต่อช้า กำลังลองเชื่อมต่อใหม่อัตโนมัติ",
        kind: "warning",
      });
    },
    markBackendDown({ commit }) {
      commit("setBackend", "down");
      commit("setBanner", {
        visible: true,
        message: "ไม่สามารถเชื่อมต่อกับระบบได้ ข้อมูลอาจไม่อัปเดต",
        kind: "error",
      });
    },
    markBackendHealthy({ commit }) {
      commit("setBackend", "healthy");
      commit("clearErrors");
      commit("setBanner", { visible: false });
      commit("resetManualRetry");
    },

    // Manual retry from the banner. Returns true on success.
    // On repeated failure (>= MAX_MANUAL_RETRIES_BEFORE_PAGE) the caller may
    // route to /connection-error so the user gets the catastrophic-fallback UX.
    async manualRetry({ commit, state, dispatch }) {
      const { retryConnection } = await import("@/libs/firestoreHealth");
      const ok = await retryConnection();
      if (ok) {
        dispatch("markBackendHealthy");
        return { ok: true, attempts: 0 };
      }
      const attempts = state.manualRetryAttempts + 1;
      commit("setManualRetryAttempts", attempts);
      if (state.backend !== "down") {
        dispatch("markBackendDown");
      }
      return {
        ok: false,
        attempts,
        shouldEscalate: attempts >= MAX_MANUAL_RETRIES_BEFORE_PAGE,
      };
    },

    // Listener-wrapper lifecycle hooks (informational; not load-bearing).
    notePendingListener({ commit }) {
      commit("incPending");
    },
    noteListenerReady({ commit }) {
      commit("decPending");
    },
  },
};
